import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adApi } from '../../api/ad.api';
import { AdWatchLocation, RewardAdConfig, UpdateRewardAdConfigRequest } from '../../types/Ad.api.type';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Save } from 'lucide-react';
import toast from 'react-hot-toast';

const LOCATION_LABELS: Record<string, string> = {
  ATTENDANCE_AD_BONUS: '출석 광고 보너스',
  ATTENDANCE_STREAK_SAVE: '출석 연속 유지 광고',
  VOTE_AD_BONUS: '투표 광고 보너스',
  DAILY_AD_BONUS: '오늘의 투표 광고 보너스',
  MILESTONE_AD_BONUS: '이번주 투표 광고 보너스',
  CREATOR_MILESTONE_AD_BONUS: '작성자 광고 보너스',
  ANALYSIS_UNLOCK: '분석 해제 광고',
  COMMENT_AD_BONUS: '댓글 광고 보너스',
  SURVEY_LIKE_AD_BONUS: '설문 좋아요 광고 보너스',
  COMMENT_LIKE_AD_BONUS: '댓글 좋아요 광고 보너스',
  EXCHANGE: '토스포인트 꺼내기 광고',
  WEEKLY_MISSION_AD_BONUS: '이번주 미션 광고 보너스',
};

const ALL_LOCATIONS: AdWatchLocation[] = [
  'VOTE_AD_BONUS', 'DAILY_AD_BONUS', 'MILESTONE_AD_BONUS',
  'ATTENDANCE_AD_BONUS', 'ATTENDANCE_STREAK_SAVE',
  'ANALYSIS_UNLOCK', 'CREATOR_MILESTONE_AD_BONUS',
  'COMMENT_AD_BONUS', 'SURVEY_LIKE_AD_BONUS', 'COMMENT_LIKE_AD_BONUS',
  'WEEKLY_MISSION_AD_BONUS', 'EXCHANGE',
];

interface LocalConfig {
  tossRewardedAdGroupId: string;
  isRewardedAdEnabled: boolean;
  tossInterstitialAdGroupId: string;
  isInterstitialAdEnabled: boolean;
  rewardedAdRatio: number;
}

const TossRewardAdConfig = () => {
  const queryClient = useQueryClient();
  const [localConfigs, setLocalConfigs] = useState<Record<string, LocalConfig>>({});

  const { data: configs, isLoading } = useQuery({
    queryKey: ['rewardAdConfigs'],
    queryFn: () => adApi.getRewardAdConfigs().then(res => res.data),
  });

  useEffect(() => {
    if (!configs) return;

    const map: Record<string, LocalConfig> = {};
    for (const loc of ALL_LOCATIONS) {
      const existing = configs.find((c: RewardAdConfig) => c.watchLocation === loc);
      map[loc] = {
        tossRewardedAdGroupId: existing?.tossRewardedAdGroupId ?? '',
        isRewardedAdEnabled: existing?.isRewardedAdEnabled ?? true,
        tossInterstitialAdGroupId: existing?.tossInterstitialAdGroupId ?? '',
        isInterstitialAdEnabled: existing?.isInterstitialAdEnabled ?? false,
        rewardedAdRatio: existing?.rewardedAdRatio ?? 100,
      };
    }
    setLocalConfigs(map);
  }, [configs]);

  const updateMutation = useMutation({
    mutationFn: ({ location, data }: { location: AdWatchLocation; data: UpdateRewardAdConfigRequest }) =>
      adApi.updateRewardAdConfig(location, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rewardAdConfigs'] });
      toast.success('설정이 저장되었습니다.');
    },
    onError: () => {
      toast.error('저장에 실패했습니다.');
    },
  });

  const handleSave = (location: AdWatchLocation) => {
    const config = localConfigs[location];
    if (!config) return;

    const data: UpdateRewardAdConfigRequest = {
      tossRewardedAdGroupId: config.tossRewardedAdGroupId || null,
      isRewardedAdEnabled: config.isRewardedAdEnabled,
      tossInterstitialAdGroupId: config.tossInterstitialAdGroupId || null,
      isInterstitialAdEnabled: config.isInterstitialAdEnabled,
      rewardedAdRatio: config.rewardedAdRatio,
    };

    updateMutation.mutate({ location, data });
  };

  const updateField = (location: string, field: keyof LocalConfig, value: string | number | boolean) => {
    setLocalConfigs(prev => ({
      ...prev,
      [location]: { ...prev[location], [field]: value },
    }));
  };

  if (isLoading) {
    return <div className="text-center py-8 text-gray-500">로딩 중...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-sm text-purple-700">
        <p className="font-medium">리워드/전면 광고 설정</p>
        <p className="mt-1">각 터치포인트별로 리워드 광고 또는 전면 광고를 설정합니다. 둘 다 활성화하면 비율에 따라 랜덤 노출됩니다.</p>
        <p className="mt-1">테스트 ID: 리워드 <code className="bg-purple-100 px-1 rounded">ait-ad-test-rewarded-id</code> / 전면 <code className="bg-purple-100 px-1 rounded">ait-ad-test-interstitial-id</code></p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {ALL_LOCATIONS.map(location => {
          const config = localConfigs[location];
          if (!config) return null;
          const bothEnabled = config.isRewardedAdEnabled && config.isInterstitialAdEnabled;

          return (
            <Card key={location}>
              <CardHeader className="pb-3">
                <span className="font-medium text-sm">{LOCATION_LABELS[location] || location}</span>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* 리워드 광고 */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-blue-600">리워드 광고</span>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-xs text-gray-500">
                        {config.isRewardedAdEnabled ? '활성' : '비활성'}
                      </span>
                      <input
                        type="checkbox"
                        checked={config.isRewardedAdEnabled}
                        onChange={e => updateField(location, 'isRewardedAdEnabled', e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </label>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">리워드 Ad Group ID</label>
                    <input
                      type="text"
                      value={config.tossRewardedAdGroupId}
                      onChange={e => updateField(location, 'tossRewardedAdGroupId', e.target.value)}
                      placeholder="토스 콘솔에서 발급받은 리워드 광고 ID"
                      className="w-full border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={!config.isRewardedAdEnabled}
                    />
                  </div>
                </div>

                {/* 전면 광고 */}
                <div className="border-t pt-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-orange-600">전면 광고</span>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-xs text-gray-500">
                        {config.isInterstitialAdEnabled ? '활성' : '비활성'}
                      </span>
                      <input
                        type="checkbox"
                        checked={config.isInterstitialAdEnabled}
                        onChange={e => updateField(location, 'isInterstitialAdEnabled', e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                      />
                    </label>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">전면 Ad Group ID</label>
                    <input
                      type="text"
                      value={config.tossInterstitialAdGroupId}
                      onChange={e => updateField(location, 'tossInterstitialAdGroupId', e.target.value)}
                      placeholder="토스 콘솔에서 발급받은 전면 광고 ID"
                      className="w-full border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      disabled={!config.isInterstitialAdEnabled}
                    />
                  </div>
                </div>

                {/* 비율 슬라이더 (둘 다 활성일 때만) */}
                {bothEnabled && (
                  <div className="border-t pt-3">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs text-gray-500">리워드/전면 비율</label>
                      <span className="text-xs font-medium">리워드 {config.rewardedAdRatio}% / 전면 {100 - config.rewardedAdRatio}%</span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={config.rewardedAdRatio}
                      onChange={e => updateField(location, 'rewardedAdRatio', Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                    />
                    <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                      <span>전면 100%</span>
                      <span>리워드 100%</span>
                    </div>
                  </div>
                )}

                {/* 저장 버튼 */}
                <Button
                  size="sm"
                  onClick={() => handleSave(location)}
                  disabled={updateMutation.isPending}
                  className="w-full flex items-center justify-center gap-1"
                >
                  <Save className="w-3.5 h-3.5" />
                  저장
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default TossRewardAdConfig;
